import { useState, useEffect, useCallback } from 'react'
import { UserPlus, Save, LogOut, Fingerprint, Trash2, ShieldCheck, Users, Wifi, Settings as SettingsIcon, Crown } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { useNavigate } from 'react-router-dom'
import { api } from '../services/api'
import { formatDate } from '../utils/format'

export default function Settings() {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const isSuperAdmin = user?.role === 'superadmin'

  const TABS = isSuperAdmin
    ? ['Profil', 'Utilisateurs', 'Empreintes', 'Activité']
    : ['Profil', 'Activité']
  const [activeTab, setActiveTab] = useState('Profil')

  // ── Profil / Financial settings ──
  const [settings, setSettings] = useState({ currency: 'XAF', monthly_income: '', savings_target_percent: '10', start_month: '' })
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  // ── Utilisateurs ──
  const [usersList, setUsersList] = useState([])
  const [regForm, setRegForm] = useState({ phone: '', name: '' })
  const [regMessage, setRegMessage] = useState('')
  const [regError, setRegError] = useState('')

  // ── Empreintes ──
  const [signers, setSigners] = useState([])

  // ── Activité ──
  const [online, setOnline] = useState([])
  const [presenceError, setPresenceError] = useState('')

  const fetchSettings = useCallback(async () => {
    try {
      const data = await api.getSettings()
      setSettings(data.settings)
    } catch {}
    setLoading(false)
  }, [])

  const fetchUsers = useCallback(async () => {
    try {
      const data = await api.getUsers()
      setUsersList(data.users || [])
    } catch {}
  }, [])

  const fetchSigners = useCallback(async () => {
    try {
      const data = await api.getSigners()
      setSigners(data.signers || [])
    } catch {}
  }, [])

  const fetchPresence = useCallback(async () => {
    try {
      const data = await api.getPresence()
      setOnline(data.online || [])
      setPresenceError('')
    } catch (e) {
      setPresenceError(e.message)
    }
  }, [])

  useEffect(() => {
    fetchSettings()
    if (isSuperAdmin) { fetchUsers(); fetchSigners() }
  }, [fetchSettings, fetchUsers, fetchSigners, isSuperAdmin])

  useEffect(() => {
    if (activeTab !== 'Activité') return
    fetchPresence()
    const id = setInterval(fetchPresence, 10000)
    return () => clearInterval(id)
  }, [activeTab, fetchPresence])

  const handleSave = async () => {
    setSaving(true)
    try {
      await api.updateSettings(settings)
      alert('Paramètres enregistrés')
    } catch (err) { alert(err.message) }
    setSaving(false)
  }

  const handleRegister = async (e) => {
    e.preventDefault()
    setRegError(''); setRegMessage('')
    try {
      await api.register(regForm.phone.trim(), regForm.name.trim())
      setRegMessage('Compte créé avec succès !')
      setRegForm({ phone: '', name: '' })
      fetchUsers()
    } catch (err) { setRegError(err.message) }
  }

  const handleDeleteSigner = async (id, name) => {
    if (!confirm(`Supprimer l'empreinte de "${name}" ?`)) return
    try {
      await api.deleteSigner(id)
      fetchSigners()
    } catch (err) { alert(err.message) }
  }

  const handleLogout = async () => {
    await logout()
    navigate('/login')
  }

  if (loading) {
    return <div className="flex justify-center py-20"><div className="w-8 h-8 border-3 border-primary-700 border-t-transparent rounded-full animate-spin" /></div>
  }

  const initials = (name) => (name || '').split(' ').map(p => p[0]).filter(Boolean).slice(0, 2).join('').toUpperCase() || '?'

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold flex items-center gap-2">
        <SettingsIcon size={20} /> Réglages
      </h2>

      {/* ── Barre d'onglets ── */}
      <div className="flex bg-slate-100 rounded-xl overflow-hidden text-sm font-medium">
        {TABS.map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`flex-1 py-2 px-1 transition-colors ${activeTab === tab ? 'bg-primary-700 text-white' : 'text-slate-500 active:bg-slate-200'}`}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* ════════════════════════════════════
          ONGLET PROFIL
      ════════════════════════════════════ */}
      {activeTab === 'Profil' && (
        <div className="space-y-4">

          {/* Carte identité */}
          <div className="card flex items-center gap-4">
            <div className="w-14 h-14 bg-primary-100 rounded-full flex items-center justify-center text-primary-700 text-xl font-bold shrink-0">
              {initials(user?.name)}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-base truncate">{user?.name}</p>
              <p className="text-sm text-slate-500 truncate">{user?.phone}</p>
              <span className={`inline-flex items-center gap-1 badge mt-1.5 ${isSuperAdmin ? 'bg-primary-100 text-primary-700' : 'bg-slate-100 text-slate-500'}`}>
                {isSuperAdmin && <Crown size={11} />}
                {isSuperAdmin ? 'Super Admin' : 'Membre'}
              </span>
            </div>
          </div>

          {/* Configuration financière */}
          <div className="card space-y-3">
            <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Configuration financière</h3>
            <div>
              <label className="label">Revenu mensuel (FCFA)</label>
              <input type="number" value={settings.monthly_income} onChange={(e) => setSettings({ ...settings, monthly_income: e.target.value })} className="input" placeholder="0" />
            </div>
            <div>
              <label className="label">Objectif d'épargne (%)</label>
              <input type="number" min="0" max="100" value={settings.savings_target_percent} onChange={(e) => setSettings({ ...settings, savings_target_percent: e.target.value })} className="input" placeholder="10" />
              <p className="text-xs text-slate-400 mt-1">Pourcentage du revenu à épargner chaque mois</p>
            </div>
            <div>
              <label className="label">Devise</label>
              <select value={settings.currency} onChange={(e) => setSettings({ ...settings, currency: e.target.value })} className="input">
                <option value="XAF">FCFA (XAF)</option>
                <option value="EUR">Euro (€)</option>
                <option value="USD">Dollar US ($)</option>
              </select>
            </div>
            <div>
              <label className="label">Mois de démarrage</label>
              <input type="month" value={settings.start_month || ''} onChange={(e) => setSettings({ ...settings, start_month: e.target.value })} className="input" />
              <p className="text-xs text-slate-400 mt-1">Premier mois officiel de suivi — utilisé pour le Cahier et les échéanciers.</p>
            </div>
            <button onClick={handleSave} disabled={saving} className="btn-primary w-full flex items-center justify-center gap-2">
              <Save size={16} /> {saving ? 'Enregistrement…' : 'Enregistrer les paramètres'}
            </button>
          </div>

          <button onClick={handleLogout} className="btn-secondary w-full flex items-center justify-center gap-2 text-red-500">
            <LogOut size={16} /> Se déconnecter
          </button>
          <p className="text-center text-xs text-slate-400">Budget Familial v1.0</p>
        </div>
      )}

      {/* ════════════════════════════════════
          ONGLET UTILISATEURS (superadmin)
      ════════════════════════════════════ */}
      {activeTab === 'Utilisateurs' && isSuperAdmin && (
        <div className="space-y-4">

          {/* Tableau des membres */}
          <div className="card">
            <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
              Membres de l'app ({usersList.length})
            </h3>
            {usersList.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-4">Aucun utilisateur trouvé</p>
            ) : (
              <div className="divide-y divide-slate-50">
                {usersList.map(u => (
                  <div key={u.id} className="flex items-center gap-3 py-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${u.role === 'superadmin' ? 'bg-primary-100 text-primary-700' : 'bg-slate-100 text-slate-600'}`}>
                      {initials(u.name)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-sm truncate">{u.name}</p>
                        {u.role === 'superadmin' && <Crown size={12} className="text-primary-600 shrink-0" />}
                      </div>
                      <p className="text-xs text-slate-400 truncate">{u.phone}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <span className={`badge ${u.role === 'superadmin' ? 'bg-primary-50 text-primary-700' : 'bg-slate-100 text-slate-500'}`}>
                        {u.role === 'superadmin' ? 'Admin' : 'Membre'}
                      </span>
                      {u.created_at && <p className="text-[10px] text-slate-300 mt-0.5">{formatDate(u.created_at)}</p>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Formulaire ajout membre */}
          <div className="card space-y-3">
            <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-2">
              <UserPlus size={14} /> Ajouter un membre
            </h3>
            <form onSubmit={handleRegister} className="space-y-3">
              <div>
                <label className="label">Nom complet</label>
                <input type="text" value={regForm.name} onChange={(e) => setRegForm({ ...regForm, name: e.target.value })} className="input" placeholder="Ex: Marie Dupont" required />
              </div>
              <div>
                <label className="label">Numéro de téléphone</label>
                <input type="tel" value={regForm.phone} onChange={(e) => setRegForm({ ...regForm, phone: e.target.value })} className="input" placeholder="+225 07 00 00 00 00" required />
              </div>
              {regError && <p className="text-red-500 text-sm bg-red-50 rounded-lg p-2">{regError}</p>}
              {regMessage && <p className="text-green-600 text-sm bg-green-50 rounded-lg p-2">{regMessage}</p>}
              <button type="submit" className="btn-primary w-full flex items-center justify-center gap-2">
                <UserPlus size={16} /> Créer le compte
              </button>
            </form>
          </div>
        </div>
      )}

      {/* ════════════════════════════════════
          ONGLET EMPREINTES (superadmin)
      ════════════════════════════════════ */}
      {activeTab === 'Empreintes' && isSuperAdmin && (
        <div className="space-y-4">

          <div className="card">
            <div className="flex items-center gap-2 mb-1">
              <Fingerprint size={16} className="text-primary-600" />
              <h3 className="font-semibold text-sm">Empreintes biométriques</h3>
            </div>
            <p className="text-xs text-slate-400 mb-4">
              Utilisées pour signer les paiements Loyer et Électricité/Eau dans le Cahier. Chaque personne a sa propre empreinte.
            </p>

            {signers.length === 0 ? (
              <div className="text-center py-6">
                <Fingerprint size={32} className="mx-auto text-slate-200 mb-2" />
                <p className="text-sm text-slate-400">Aucune empreinte enregistrée</p>
                <p className="text-xs text-slate-300 mt-1">Allez dans le <strong>Cahier</strong> → bouton &quot;+ Signataire&quot;</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-50">
                {signers.map(s => (
                  <div key={s.id} className="flex items-center gap-3 py-3">
                    <div className="w-9 h-9 rounded-full bg-green-50 flex items-center justify-center shrink-0">
                      <ShieldCheck size={18} className="text-green-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm">{s.signer_name}</p>
                      <p className="text-xs text-slate-400">{s.created_at ? formatDate(s.created_at) : '—'}</p>
                    </div>
                    <button
                      onClick={() => handleDeleteSigner(s.id, s.signer_name)}
                      className="p-2 rounded-lg text-slate-300 active:text-red-500 active:bg-red-50 transition-colors"
                      title="Supprimer cette empreinte"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="card bg-amber-50 border-amber-100">
            <p className="text-xs text-amber-700 leading-relaxed">
              <strong>⚠️ Attention :</strong> Supprimer une empreinte n&apos;annule pas les paiements déjà enregistrés dans le Cahier. En cas de ré-enregistrement, la personne devra re-signer les paiements du mois en cours si besoin.
            </p>
          </div>
        </div>
      )}

      {/* ════════════════════════════════════
          ONGLET ACTIVITÉ
      ════════════════════════════════════ */}
      {activeTab === 'Activité' && (
        <div className="space-y-4">
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-semibold flex items-center gap-2"><Users size={16} /> Qui est connecté ?</h3>
                <p className="text-xs text-slate-400 mt-0.5">Membres actifs dans les 2 dernières minutes</p>
              </div>
              <button onClick={fetchPresence} className="btn-secondary text-xs flex items-center gap-1.5">
                <Wifi size={13} /> Rafraîchir
              </button>
            </div>

            {presenceError && <p className="text-sm text-red-500 bg-red-50 rounded-lg p-2 mb-3">{presenceError}</p>}

            {online.length === 0 ? (
              <div className="text-center py-6">
                <Users size={28} className="mx-auto text-slate-200 mb-2" />
                <p className="text-sm text-slate-400">Aucun membre en ligne</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-50">
                {online.map(u => (
                  <div key={u.user_id} className="flex items-center gap-3 py-3">
                    <div className="relative">
                      <div className="w-10 h-10 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-sm font-semibold">
                        {initials(u.name)}
                      </div>
                      <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-500 rounded-full border-2 border-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{u.name}</p>
                      <p className="text-xs text-slate-400 truncate">{u.route || '—'}</p>
                    </div>
                    <span className="text-xs text-slate-400 shrink-0">
                      {new Date(u.ts).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
